import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

interface ValidationRulesDialogProps {
  open: boolean;
  onClose: () => void;
  rulesEditorContext: { tableName: string; columnName: string } | null;
  editPayload: any;
  onEditPayloadChange: (payload: any) => void;
}

interface EnumAllowedValuesEditorProps {
  values: string[];
  onChange: (next: string[]) => void;
}

/** One value per chip; type and press Enter to add. Backspace on empty input removes the last chip. */
const EnumAllowedValuesEditor: React.FC<EnumAllowedValuesEditorProps> = ({ values, onChange }) => {
  const [draft, setDraft] = React.useState('');

  const commitDraft = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) {
      setDraft('');
      return;
    }
    onChange([...values, v]);
    setDraft('');
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        Allowed values — type a value and press Enter to add a tag
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          alignItems: 'center',
          alignContent: 'center',
          minHeight: 44,
        }}
      >
        {values.map((v, i) => (
          <Chip
            key={`${v}-${i}`}
            label={v}
            size="small"
            onDelete={() => onChange(values.filter((_, j) => j !== i))}
          />
        ))}
        <TextField
          size="small"
          variant="standard"
          placeholder={values.length === 0 ? 'e.g. active — then Enter' : 'Add another…'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitDraft();
            } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
              onChange(values.slice(0, -1));
            }
          }}
          sx={{ flex: '1 1 140px', minWidth: 140, '& .MuiInput-root': { mt: 0 } }}
        />
      </Paper>
    </Box>
  );
};

/** Canonical rule-discriminator values shown under the "Basic Validation"
 *  UI group. Read via :func:`ruleKind` so legacy recipes that still carry
 *  ``rule.rule`` instead of Phase 3's ``rule.type`` still classify correctly. */
const BASIC_RULE_IDS = ['not_empty', 'numeric', 'before_date', 'after_date'] as const;

/** Return the rule's discriminator regardless of shape. Phase 3 typed
 *  rules carry ``type``; pre-Phase-3 recipes used ``rule``. */
const ruleKind = (rule: any): string => (rule?.type ?? rule?.rule ?? '');

const llmToUiRuleType = (llmRule: string): string => {
  const mapping: Record<string, string> = {
    regex: 'format',
    pattern: 'format',
    in_list: 'enum',
    not_empty: 'basic',
    numeric: 'basic',
    date_format: 'format',
    datetime_format: 'format',
    before_date: 'basic',
    after_date: 'basic',
    // Hierarchical output rule types (generic terminology)
    allowed_values: 'enum',
    cross_reference: 'cross_reference',
    data_type: 'basic',
    length: 'length',
  };
  return mapping[llmRule] || llmRule;
};

const EMPTY_DRAFT_RULE = {
  type: 'regex',
  pattern: '',
  message: '',
};

const ValidationRulesDialog: React.FC<ValidationRulesDialogProps> = ({
  open,
  onClose,
  rulesEditorContext,
  editPayload,
  onEditPayloadChange,
}) => {
  const [draftRule, setDraftRule] = React.useState<Record<string, any> | null>(null);

  React.useEffect(() => {
    if (!open) setDraftRule(null);
  }, [open]);

  React.useEffect(() => {
    setDraftRule(null);
  }, [rulesEditorContext?.tableName, rulesEditorContext?.columnName]);

  const columnData =
    rulesEditorContext && editPayload?.tables?.[rulesEditorContext.tableName]?.columns?.[rulesEditorContext.columnName];

  const updateRule = (idx: number, field: string, value: any) => {
    const updated = { ...editPayload };
    updated.tables[rulesEditorContext!.tableName].columns[rulesEditorContext!.columnName].validation_rules[idx][field] = value;
    onEditPayloadChange(updated);
  };

  const commitDraftRule = () => {
    if (!draftRule || !rulesEditorContext) return;
    const updated = { ...editPayload };
    const column = updated.tables[rulesEditorContext.tableName].columns[rulesEditorContext.columnName];
    if (!column.validation_rules) column.validation_rules = [];
    column.validation_rules.push({ ...draftRule });
    onEditPayloadChange(updated);
    setDraftRule(null);
  };

  const handleDone = () => {
    setDraftRule(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDone} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, pr: 1 }}>
        <Box>
          Edit Validation Rules
          {rulesEditorContext && (
            <Typography variant="caption" display="block" color="text.secondary">
              {rulesEditorContext.tableName}.{rulesEditorContext.columnName}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={handleDone} aria-label="Close validation rules dialog" sx={{ mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {columnData && (
          <Stack spacing={2}>
            <Alert severity="info">
              Define validation rules for this column. Each rule will be checked during data loading.
            </Alert>
            {(columnData.validation_rules || []).map((rule: any, idx: number) => {
              const kind = ruleKind(rule) || 'format';
              const uiRuleType = llmToUiRuleType(kind);
              const isBasicRule = ['not_empty', 'numeric', 'before_date', 'after_date'].includes(kind);
              return (
                <RuleEditor
                  key={idx}
                  rule={rule}
                  uiRuleType={uiRuleType}
                  isBasicRule={isBasicRule}
                  onRuleChange={(field, value) => updateRule(idx, field, value)}
                  onDeleteRule={() => {
                    const updated = { ...editPayload };
                    updated.tables[rulesEditorContext!.tableName].columns[rulesEditorContext!.columnName].validation_rules.splice(idx, 1);
                    onEditPayloadChange(updated);
                  }}
                />
              );
            })}

            {draftRule && (
              <Box sx={{ p: 2, border: 1, borderColor: 'primary.main', borderRadius: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2">
                    New Validation Rule
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click Save Rule to add — you can add more before closing
                  </Typography>
                </Stack>
                <RuleEditor
                  rule={draftRule}
                  embedded
                  uiRuleType={llmToUiRuleType(ruleKind(draftRule) || 'format')}
                  isBasicRule={['not_empty', 'numeric', 'before_date', 'after_date'].includes(ruleKind(draftRule))}
                  onRuleChange={(field, value) => setDraftRule((prev) => (prev ? { ...prev, [field]: value } : prev))}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                  <Button size="small" onClick={() => setDraftRule(null)}>
                    Cancel
                  </Button>
                  <Button size="small" variant="contained" onClick={commitDraftRule}>
                    Save Rule
                  </Button>
                </Stack>
              </Box>
            )}

            <Button
              startIcon={<AddIcon />}
              disabled={!!draftRule}
              onClick={() => setDraftRule({ ...EMPTY_DRAFT_RULE })}
            >
              Add Validation Rule
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDone}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

interface RuleEditorProps {
  rule: any;
  uiRuleType: string;
  isBasicRule: boolean;
  onRuleChange: (field: string, value: any) => void;
  onDeleteRule?: () => void;
  /** When true, omit the outer card — used for unsaved draft forms. */
  embedded?: boolean;
}

const RuleEditor: React.FC<RuleEditorProps> = ({
  rule,
  uiRuleType,
  isBasicRule,
  onRuleChange,
  onDeleteRule,
  embedded = false,
}) => {
  const fields = (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          label="Rule Type"
          select
          size="small"
          value={uiRuleType}
          onChange={(e) => {
            const next = e.target.value;
            const current = ruleKind(rule);
            if (next === 'basic') {
              const keep =
                typeof current === 'string' &&
                (BASIC_RULE_IDS as readonly string[]).includes(current);
              onRuleChange('type', keep ? current : 'not_empty');
            } else if (next === 'format') {
              // UI's "format" maps to Phase 3 ``regex`` typed rule.
              onRuleChange('type', 'regex');
            } else {
              onRuleChange('type', next);
            }
          }}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="format">Format/Pattern</MenuItem>
          <MenuItem value="range">Range</MenuItem>
          <MenuItem value="length">Length</MenuItem>
          <MenuItem value="enum">Enum/Allowed Values</MenuItem>
          <MenuItem value="basic">Basic Validation</MenuItem>
          <MenuItem value="custom">Custom Expression</MenuItem>
        </TextField>
        {onDeleteRule && (
          <IconButton size="small" color="error" onClick={onDeleteRule}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {uiRuleType !== ruleKind(rule) && !isBasicRule && (
        <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
          Original LLM rule type: <strong>{ruleKind(rule)}</strong>
        </Alert>
      )}

      {uiRuleType === 'basic' && (
        <TextField
          label="Basic Rule Type"
          select
          size="small"
          fullWidth
          value={ruleKind(rule) || 'not_empty'}
          onChange={(e) => onRuleChange('type', e.target.value)}
        >
          <MenuItem value="not_empty">Not Empty</MenuItem>
          <MenuItem value="numeric">Numeric</MenuItem>
          <MenuItem value="before_date">Before Date (field comparison)</MenuItem>
          <MenuItem value="after_date">After Date (field comparison)</MenuItem>
        </TextField>
      )}

      {uiRuleType === 'basic' && (ruleKind(rule) === 'before_date' || ruleKind(rule) === 'after_date') && (
        <TextField
          label="Compare to Field"
          size="small"
          fullWidth
          value={rule.field || ''}
          onChange={(e) => onRuleChange('field', e.target.value)}
          placeholder="e.g., enrollment_date"
          helperText="Name of the field to compare against"
        />
      )}

      {uiRuleType === 'format' && (
        <TextField
          label="Pattern (regex)"
          size="small"
          fullWidth
          value={rule.pattern || ''}
          onChange={(e) => onRuleChange('pattern', e.target.value)}
          placeholder="e.g., ^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$"
        />
      )}

      {ruleKind(rule) === 'range' && (
        <Stack direction="row" spacing={2}>
          <TextField
            label="Min"
            size="small"
            type="number"
            value={rule.min ?? ''}
            onChange={(e) => onRuleChange('min', parseFloat(e.target.value))}
          />
          <TextField
            label="Max"
            size="small"
            type="number"
            value={rule.max ?? ''}
            onChange={(e) => onRuleChange('max', parseFloat(e.target.value))}
          />
        </Stack>
      )}

      {ruleKind(rule) === 'length' && (
        <Stack direction="row" spacing={2}>
          <TextField
            label="Min Length"
            size="small"
            type="number"
            value={rule.min_length ?? ''}
            onChange={(e) => onRuleChange('min_length', parseInt(e.target.value))}
          />
          <TextField
            label="Max Length"
            size="small"
            type="number"
            value={rule.max_length ?? ''}
            onChange={(e) => onRuleChange('max_length', parseInt(e.target.value))}
          />
        </Stack>
      )}

      {uiRuleType === 'enum' && (
        <EnumAllowedValuesEditor
          // Phase 3 typed-rule shape writes ``values``; legacy recipes
          // may still carry ``allowed_values`` — read both transitionally.
          values={rule.values || rule.allowed_values || []}
          onChange={(valueArray) => onRuleChange('values', valueArray)}
        />
      )}

      {uiRuleType === 'custom' && (
        <TextField
          label="Custom Expression"
          size="small"
          fullWidth
          value={rule.expression || ''}
          onChange={(e) => onRuleChange('expression', e.target.value)}
          placeholder="e.g., value > 0 AND value < 100"
          helperText={
            'SQL-style expression evaluated per cell. Use the identifier value for this column ' +
            '(e.g. value IS NOT NULL, LENGTH(CAST(value AS VARCHAR)) <= 10).'
          }
        />
      )}

      <TextField
        label="Error Message"
        size="small"
        fullWidth
        value={rule.message || ''}
        onChange={(e) => onRuleChange('message', e.target.value)}
        placeholder="Message shown when validation fails"
      />
    </Stack>
  );

  if (embedded) return fields;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {fields}
    </Paper>
  );
};

export default ValidationRulesDialog;
